import { useCallback, useState } from "react";
import axios from "axios";
import { customMetricConst, Metric } from "../types/api/customMetric";
import { useMessage } from "./useMessage";
import { useLoginUser } from "./useLoginUser";
import appconfig from "../config.json";
import {
  Datapoint,
  ResponseGetMetricStatistics,
  MetricDataResponse,
  MetricDataResult,
} from "../types/api/metricDataResult";
type MetricsItem = { Metrics: Metric[] };

export const useCustomMetrics = () => {
  const { showMessage } = useMessage();
  const [loading, setLoading] = useState(false);
  const { loginUser, tokenRefresh, selectTenant } = useLoginUser();

  const [metrics, setMetrics] = useState<Array<Metric>>([]);
  const [datapoints, setDatapoints] = useState<Array<Datapoint>>([]);
  const [metricDataResults, setMetricDataResults] = useState<Array<MetricDataResult>>([]);
  const MANAGER_API_URL: string = appconfig["IOT_MANAGEMENT_API_URL"];

  const getMetrics = useCallback(() => {
    setLoading(true);
    let atr: string = "";
    if (selectTenant != "999999") {
      atr = "?tenant=" + selectTenant;
    }
    new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
      axios
        .get<MetricsItem>(`${MANAGER_API_URL}/custommetric/listmetrics` + atr, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(async (res) => {
          if (res.status === 200) {
            // Convert to an array of { messageId, config }
            setMetrics(res.data.Metrics);
            setLoading(false);
          } else if (res.status === 403) {
            showMessage({ title: "権限エラー:" + res.data, status: "error" });
            setLoading(false);
          } else {
            throw new Error(res.statusText);
          }
        })
        .catch(() =>
          showMessage({
            title: "メトリック一覧の取得に失敗しました",
            status: "error",
          })
        )
        .finally(() => setLoading(false));
    });
  }, []);

  const getConvertCountMetric = useCallback(
    (
      tenant: string,
      entitytype: string | undefined,
      entityid: string | undefined,
      drivername: string | undefined,
      starttime: string,
      endtime: string,
      period: number
    ) => {
      setLoading(true);

      const atr: string =
        "?tenant=" +
        tenant +
        (entitytype ? "&entitytype=" + entitytype : "") +
        (entityid ? "&entityid=" + entityid : "") +
        (drivername ? "&drivername=" + drivername : "") +
        "&starttime=" +
        starttime +
        "&starttime=" +
        starttime +
        "&endtime=" +
        endtime +
        "&period=" +
        String(period);
      new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
        axios
          .get<ResponseGetMetricStatistics>(
            MANAGER_API_URL + "/custommetric/convertcount" + atr,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
          .then(async (res) => {
            if (res.status === 200) {
              setDatapoints(res.data.Datapoints);
              setDatapoints(
                res.data.Datapoints.sort((a, b) => {
                  if (a.Timestamp > b.Timestamp) return 1;
                  else return -1;
                })
              );
              setLoading(false);
            } else if (res.status === 403) {
              showMessage({ title: "権限エラー:" + res.data, status: "error" });
              setLoading(false);
            } else {
              throw new Error(res.statusText);
            }
          })
          .catch(() =>
            showMessage({
              title: "メトリクスの取得に失敗しました",
              status: "error",
            })
          )
          .finally(() => setLoading(false));
      });
    },
    []
  );
  const getConvertCountJobMetric = useCallback(
    (
      tenant: string,
      batchName: string | undefined,
      starttime: string,
      endtime: string,
      period: number
    ) => {
      setLoading(true);

      const atr: string =
        "?tenant=" +
        tenant +
        (batchName ? "&batchname=" + batchName : "") +
        "&starttime=" +
        starttime +
        "&starttime=" +
        starttime +
        "&endtime=" +
        endtime +
        "&period=" +
        String(period);
      console.log('[useCustomMetrics] API URL:', MANAGER_API_URL + "/metricdata" + atr);
      new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
        axios
          .get<MetricDataResponse>(
            MANAGER_API_URL + "/metricdata" + atr,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
          .then(async (res) => {
            console.log('[useCustomMetrics] Response:', res.status, res.data);
            if (res.status === 200) {
              // 新しいデータ形式を処理
              if (res.data.MetricDataResults && res.data.MetricDataResults.length > 0) {
                // オリジナルのMetricDataResultsを保存
                console.log('[useCustomMetrics] Setting results:', res.data.MetricDataResults.length);
                setMetricDataResults(res.data.MetricDataResults);
                
                // MetricDataResultsからDatapoint形式に変換
                const convertedDatapoints: Datapoint[] = [];
                
                // 最初のメトリックのタイムスタンプを基準にする
                const metricResult = res.data.MetricDataResults[0];
                const timestamps = metricResult.Timestamps;
                
                // 各タイムスタンプに対応するデータポイントを作成
                for (let i = 0; i < timestamps.length; i++) {
                  const timestamp = timestamps[i];
                  const datapoint: Datapoint = {
                    Timestamp: timestamp,
                    Sum: 0, // デフォルト値を設定
                    Unit: "Count" // デフォルトのユニットを設定
                  };
                  
                  // 各メトリックの値を合計する
                  res.data.MetricDataResults.forEach((metric: MetricDataResult) => {
                    if (i < metric.Values.length) {
                      // Sumプロパティに値を加算する
                      datapoint.Sum = (datapoint.Sum || 0) + metric.Values[i];
                    }
                  });
                  
                  convertedDatapoints.push(datapoint);
                }
                
                // データポイントを時間順にソート
                const sortedDatapoints = convertedDatapoints.sort((a, b) => {
                  if (a.Timestamp > b.Timestamp) return 1;
                  else return -1;
                });
                
                setDatapoints(sortedDatapoints);
              } else {
                // データがない場合は空の配列を設定
                console.log('[useCustomMetrics] No data in response');
                setDatapoints([]);
                setMetricDataResults([]);
              }
              setLoading(false);
            } else if (res.status === 403) {
              showMessage({ title: "権限エラー:" + res.data, status: "error" });
              setLoading(false);
            } else {
              throw new Error(res.statusText);
            }
          })
          .catch((error) => {
            console.error('[useCustomMetrics] Error:', error);
            showMessage({
              title: "メトリクスの取得に失敗しました",
              status: "error",
            });
          })
          .finally(() => setLoading(false));
      });
    },
    []
  );

  const getGenericMetric = useCallback(
    (
      namespace: string,
      metricName: string,
      tenant: string,
      resourceId: string,
      starttime: string,
      endtime: string,
      period: number,
      stat: string = 'Sum'
    ) => {
      setLoading(true);

      const atr = `?namespace=${namespace}&metricname=${metricName}&tenant=${tenant}&resourceid=${resourceId}&starttime=${starttime}&endtime=${endtime}&period=${period}&stat=${stat}`;
      
      new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
        axios
          .get<MetricDataResponse>(MANAGER_API_URL + "/metricdata" + atr, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then(async (res) => {
            if (res.status === 200) {
              if (res.data.MetricDataResults && res.data.MetricDataResults.length > 0) {
                setMetricDataResults(res.data.MetricDataResults);
              } else {
                setMetricDataResults([]);
              }
              setLoading(false);
            } else if (res.status === 403) {
              showMessage({ title: "権限エラー:" + res.data, status: "error" });
              setLoading(false);
            } else {
              throw new Error(res.statusText);
            }
          })
          .catch((error) => {
            console.error('[useCustomMetrics] Error:', error);
            showMessage({
              title: "メトリクスの取得に失敗しました",
              status: "error",
            });
          })
          .finally(() => setLoading(false));
      });
    },
    []
  );

  return {
    getMetrics,
    getConvertCountMetric,
    getConvertCountJobMetric,
    getGenericMetric,
    loading,
    metrics,
    datapoints,
    metricDataResults,
  };
};
